import {Injectable} from '@angular/core';
import {TradingInstrument} from '../../trading-instruments/trading-instrument.class';
import {TradingInstrumentKind} from '../../trading-instruments/trading-instrument-kind.enum';
import {TradingInstrumentsService} from '../../trading-instruments/trading-instruments-service.interface';

/**
 * Normalization utility for multi-asset hedging grid.
 *
 * Converts values between different underlyings so they can be displayed
 * and compared in a common unit (SPX-equivalent points).
 *
 * The effective dollar multiplier per point depends on instrument kind:
 * - Futures: pointValue directly (e.g., ES = $50/point)
 * - Non-Futures: pointValue * 100 (e.g., SPX = $1 * 100 = $100/point)
 *
 * Normalization ratio = target_effective / base_effective
 * For ES→SPX: 50/100 = 0.5
 */
@Injectable()
export class HgNormalizationService {

    constructor(
        private readonly _tiService: TradingInstrumentsService
    ) {
    }

    /**
     * Gets the effective dollar multiplier per option point for a given underlying.
     * Follows the same convention as multi-leg-order.class.ts marketValue getter.
     */
    getEffectiveMultiplier(instrument: TradingInstrument): number {
        if (!instrument) {
            return 100; // Default to standard equity option multiplier
        }

        if (instrument.kind === TradingInstrumentKind.Futures) {
            return instrument.pointValue;
        }

        return instrument.pointValue * 100;
    }

    /**
     * Gets the point value ratio between a target underlying and the base underlying.
     * Used to normalize display values to base-underlying-equivalent.
     *
     * @param targetUnderlying The underlying to normalize FROM (e.g., 'ESH6')
     * @param baseUnderlying The underlying to normalize TO (e.g., 'SPX')
     * @returns Ratio to multiply target values by (e.g., 0.5 for ES→SPX)
     */
    getPointValueRatio(targetUnderlying: string, baseUnderlying: string): number {
        if (targetUnderlying === baseUnderlying) {
            return 1;
        }

        const targetInstrument = this._tiService.getInstrumentByTicker(targetUnderlying);
        const baseInstrument = this._tiService.getInstrumentByTicker(baseUnderlying);

        const targetMultiplier = this.getEffectiveMultiplier(targetInstrument);
        const baseMultiplier = this.getEffectiveMultiplier(baseInstrument);

        if (baseMultiplier === 0) {
            return 1;
        }

        return targetMultiplier / baseMultiplier;
    }

    /**
     * Gets the quantity multiplier for achieving equivalent exposure.
     * Inverse of point value ratio.
     *
     * @param targetUnderlying The underlying to hedge with (e.g., 'ESH6')
     * @param baseUnderlying The base underlying (e.g., 'SPX')
     * @returns Multiplier for quantity (e.g., 2 for ES→SPX, meaning 2x ES contracts)
     */
    getQtyMultiplier(targetUnderlying: string, baseUnderlying: string): number {
        const ratio = this.getPointValueRatio(targetUnderlying, baseUnderlying);
        if (ratio === 0) {
            return 1;
        }
        return 1 / ratio;
    }

    /**
     * Normalizes a value (cost, P&L, etc.) from a target underlying to base-equivalent.
     * @param value The raw value in target underlying's point terms
     * @param ratio The point value ratio (from getPointValueRatio)
     * @returns The normalized value in base underlying's equivalent terms
     */
    normalizeValue(value: number, ratio: number): number {
        if (value === null || value === undefined) {
            return value;
        }
        return value * ratio;
    }
}
